// ===========================================
// Calendar Custom Toolbar
// ===========================================

import { Views, Navigate, ToolbarProps } from 'react-big-calendar';
import { Term } from '../../../api/admin.api';
import {
  Box,
  Button,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ResourceViewMode } from './calendar.types';

interface CalendarToolbarComponentProps extends ToolbarProps {
  resourceViewMode: ResourceViewMode;
  onResourceViewModeChange: (
    event: React.MouseEvent<HTMLElement>,
    newMode: ResourceViewMode | null
  ) => void;
  hideResourceToggle?: boolean;
  onSettingsClick: () => void;
  onLegendClick: () => void;
  terms?: Term[];
}

function toLocalDay(date: Date): number {
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

function parseDateStr(str: string): number {
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return y * 10000 + m * 100 + d;
}

function getWeekLabel(date: Date, terms: Term[]): string | null {
  const day = toLocalDay(date);
  for (const term of terms) {
    const start = parseDateStr(term.startDate);
    const end = parseDateStr(term.endDate);
    if (day >= start && day <= end) {
      const [sy, sm, sd] = term.startDate.split('T')[0].split('-').map(Number);
      const startMs = Date.UTC(sy, sm - 1, sd);
      // Monday-anchored weeks (termWeekAnchor): number from the Monday of
      // the term-start week so labels match the canonical numbering.
      const anchorMs = startMs - ((new Date(startMs).getUTCDay() + 6) % 7) * 86400000;
      const dayMs = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
      const week = Math.floor((dayMs - anchorMs) / (7 * 24 * 60 * 60 * 1000)) + 1;
      return `Week ${week}`;
    }
  }
  return null;
}

export default function CalendarToolbar({
  label,
  date,
  onNavigate,
  onView,
  view: currentView,
  resourceViewMode,
  onResourceViewModeChange,
  hideResourceToggle = false,
  onSettingsClick,
  onLegendClick,
  terms = [],
}: CalendarToolbarComponentProps) {
  const weekLabel = currentView !== Views.MONTH ? getWeekLabel(date, terms) : null;
  // Deliberately NOT className="rbc-toolbar": the library's CSS for that class
  // stacks every control in a column below 768px, and its `.rbc-toolbar button`
  // rule outranks MUI's, which removed the highlight on the selected view and
  // drew a box around the icon buttons.
  return (
    <Box
      data-testid="calendar-toolbar"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 2,
        // The theme's 10px 24px button padding applies at size="small" too, and
        // is too wide for these rows on a phone.
        '& .MuiButton-root': { minWidth: 0, px: 1.25, py: 0.75 },
      }}
    >
      {/* Current date label */}
      <Typography variant="h6" sx={{ textAlign: 'center' }}>
        {weekLabel ? `${weekLabel}, ${label}` : label}
      </Typography>

      {/* Controls: one row where they fit, wrapping as whole groups on a phone */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'space-between' },
          gap: 1,
        }}
      >
        {/* Navigation, with the column toggle beside it */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" variant="outlined" onClick={() => onNavigate(Navigate.TODAY)}>
              Today
            </Button>
            <Button size="small" variant="outlined" onClick={() => onNavigate(Navigate.PREVIOUS)}>
              Back
            </Button>
            <Button size="small" variant="outlined" onClick={() => onNavigate(Navigate.NEXT)}>
              Next
            </Button>
          </Box>

          {/* Resource view toggle (day view with columns only: narrowed to one teacher there are none) */}
          {currentView === Views.DAY && !hideResourceToggle && (
            <ToggleButtonGroup
              value={resourceViewMode}
              exclusive
              onChange={onResourceViewModeChange}
              size="small"
              aria-label="Resource view"
            >
              <ToggleButton value="teacher" sx={{ py: 0.5, px: 1 }} aria-label="Teacher view">
                <PersonIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="room" sx={{ py: 0.5, px: 1 }} aria-label="Room view">
                <MeetingRoomIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {/* View selector buttons */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              size="small"
              variant={currentView === Views.MONTH ? 'contained' : 'outlined'}
              onClick={() => onView(Views.MONTH)}
            >
              Month
            </Button>
            <Button
              size="small"
              variant={currentView === Views.WEEK ? 'contained' : 'outlined'}
              onClick={() => onView(Views.WEEK)}
            >
              Week
            </Button>
            <Button
              size="small"
              variant={currentView === Views.DAY ? 'contained' : 'outlined'}
              onClick={() => onView(Views.DAY)}
            >
              Day
            </Button>
          </Box>

          {/* Settings and legend stay together */}
          <Box data-testid="calendar-toolbar-icons" sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Display Settings">
              <IconButton onClick={onSettingsClick}>
                <SettingsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Legend">
              <IconButton onClick={onLegendClick}>
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
